/**
 * CONVOY TOW — the state machine.
 *
 * Nothing in react-router keeps the outgoing page mounted: the moment the
 * location changes, `useOutlet()` returns the new element and the old one is
 * gone. A coupled track needs BOTH pages at once, so this hook holds the
 * previous outlet element in state for the length of the tow and then drops it.
 *
 * (`unstable_viewTransition` is not an option: it snapshots the old DOM as a
 * static image and unmounts the live page, which cannot be a live coupled
 * track. It was evaluated and rejected.)
 *
 * The gate is deliberately narrow. Exactly one transition tows —
 * /marketplace → /marketplace/search, pushed, with motion allowed. Everything
 * else in the app returns `framed: false` and renders the bare outlet, which is
 * byte-for-byte what it does today.
 */
import {
  type ReactNode,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import {
  NavigationType,
  useLocation,
  useNavigationType,
  useOutlet,
} from 'react-router-dom';

import {
  FLIGHT_TIMING,
  onPinFlight,
  prefersReducedMotion,
} from '../pin-flight/pin-flight-contract';
import {
  CARRIAGE_END_MS,
  carriageDelayMs,
  towDelayMs,
} from './page-tow-styles';

/* ------------------------------------------------------------------ *
 * The gate
 * ------------------------------------------------------------------ */

const TOW_FROM = '/marketplace';
const TOW_TO = '/marketplace/search';

/**
 * Trailing slashes only; the query string is intentionally ignored (the
 *  results page re-searches by changing it, and that must never re-tow).
 * @param pathname
 */
function normalize(pathname: string): string {
  return pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
}

/**
 * True for the two paths that get the tow chassis at all.
 * @param pathname
 */
function isFramedPath(pathname: string): boolean {
  const p = normalize(pathname);
  return p === TOW_FROM || p === TOW_TO;
}

function isTowPair(from: string, to: string): boolean {
  return normalize(from) === TOW_FROM && normalize(to) === TOW_TO;
}

/* ------------------------------------------------------------------ *
 * The scroll container
 * ------------------------------------------------------------------ */

/**
 * The nearest scrolling ancestor. This differs by shell and must not be
 * assumed: signed-in buyers get ProducerShell's `<Box component="main">`, which
 * is `overflow: auto` and therefore scrolls itself, while guests hit App's
 * plain branch where the document scrolls. `null` means the document.
 * @param el
 */
function findScrollContainer(el: HTMLElement): HTMLElement | null {
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const { overflowY } = window.getComputedStyle(node);
    if (
      overflowY === 'auto' ||
      overflowY === 'scroll' ||
      overflowY === 'hidden'
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

interface Freeze {
  /** Visible height to pin the clip window to for the length of the tow. */
  height: number;
  /** The scroll offset the container is about to lose (see outgoingSlotSx). */
  scrollOffset: number;
  /**
   * Was it actually scrolling? If so its scrollbar is about to vanish and the
   *  gutter has to be held open, or the whole page jumps sideways mid-tow.
   */
  wasScrollable: boolean;
}

/** A floor, so a mis-measure can never collapse the track to nothing. */
const MIN_FROZEN_HEIGHT = 320;

/**
 * Measure BEFORE the tow styles are applied.
 *
 * This is called during the render that starts the tow, which is the last
 * moment the DOM still holds the outgoing page at its real scroll position —
 * a layout effect would be one commit too late and would measure the frozen
 * geometry it was supposed to capture.
 * @param viewport
 */
function measureFreeze(viewport: HTMLElement): Freeze {
  const rect = viewport.getBoundingClientRect();
  const container = findScrollContainer(viewport);

  const scrollOffset = container ? container.scrollTop : window.scrollY;
  const containerTop = container ? container.getBoundingClientRect().top : 0;
  const visibleHeight = container ? container.clientHeight : window.innerHeight;
  const scrollHeight = container
    ? container.scrollHeight
    : document.documentElement.scrollHeight;

  // Where the viewport lands once the container's content shrinks to one screen
  // and the browser clamps its scroll back to 0.
  const topWithin = rect.top - containerTop + scrollOffset;

  return {
    height: Math.max(MIN_FROZEN_HEIGHT, visibleHeight - topWithin),
    scrollOffset,
    wasScrollable: scrollHeight > visibleHeight + 1,
  };
}

/* ------------------------------------------------------------------ *
 * State
 * ------------------------------------------------------------------ */

type TowPhase =
  /** No tow. The chassis is inert; on unframed paths it is not even rendered. */
  | 'idle'
  /** Both pages coupled on the track. Ends at TOUCHDOWN. */
  | 'towing'
  /** Track dismantled, carriages still trailing in. Ends at CARRIAGE_END_MS. */
  | 'settling';

interface TowState {
  /** The pathname this state describes — the hook's memory of "where we were". */
  path: string;
  phase: TowPhase;
  /** Bumped per tow so a stale timer from an interrupted run can be ignored. */
  runId: number;
  startedAt: number;
  prevPath: string | null;
  prevElement: ReactNode;
  frozenHeight: number | null;
  scrollOffset: number;
  wasScrollable: boolean;
  /** How late the navigation was relative to the Search click (see below). */
  shiftMs: number;
}

const IDLE: Omit<TowState, 'path' | 'runId'> = {
  phase: 'idle',
  startedAt: 0,
  prevPath: null,
  prevElement: null,
  frozenHeight: null,
  scrollOffset: 0,
  wasScrollable: false,
  shiftMs: 0,
};

/**
 * A pin flight older than this is not the cause of this navigation, so its
 * timestamp must not be used to shift the tow.
 */
const MAX_ANCHOR_AGE_MS = 1000;

export interface PageTow {
  /**
   * Render the chassis at all. False everywhere except the two marketplace
   *  paths, where it is two style-less divs until a tow actually starts.
   */
  framed: boolean;
  towing: boolean;
  /**
   * Towing OR settling — the window in which the carriage rules must stay
   *  mounted and unchanged.
   */
  arriving: boolean;
  path: string;
  element: ReactNode;
  outgoing: { path: string; element: ReactNode } | null;
  frozenHeight: number | null;
  scrollOffset: number;
  /** Animation delays, already corrected for a late navigation. */
  towDelay: number;
  carriageDelay: number;
  viewportRef: React.RefObject<HTMLDivElement>;
  outgoingRef: React.RefObject<HTMLDivElement>;
}

export function usePageTow(): PageTow {
  const outlet = useOutlet();
  const { pathname } = useLocation();
  const navigationType = useNavigationType();

  const viewportRef = useRef<HTMLDivElement>(null);
  const outgoingRef = useRef<HTMLDivElement>(null);
  /**
   * The outlet element as of the last COMMITTED render — i.e. the page that is
   *  still on screen while we render its replacement. Written in a layout
   *  effect, never during render: StrictMode double-renders, and a ref mutated
   *  in render would hand the second pass the incoming page as the outgoing one.
   */
  const outletRef = useRef<ReactNode>(outlet);
  const flightAtRef = useRef<number | null>(null);

  const [state, setState] = useState<TowState>(() => ({
    ...IDLE,
    path: pathname,
    runId: 0,
  }));

  /**
   * t=0 for the whole gesture is the Search CLICK, not this navigation. They
   * are normally the same tick (the trigger calls requestPinFlight() and then
   * navigate()), but anchoring to the click is what actually guarantees
   * TOW_END === TOUCHDOWN if anything ever lands between them: the pin is
   * already flying on the click's clock and will plant at TOUCHDOWN regardless
   * of when we started.
   */
  useEffect(
    () =>
      onPinFlight(() => {
        flightAtRef.current = performance.now();
      }),
    [],
  );

  // ---- transition detection (render phase) --------------------------------
  // Deriving state during render rather than in an effect: an effect would
  // commit one frame of the new page ALONE before the track appeared, which is
  // a visible flash of the destination before the transition that introduces it.
  if (state.path !== pathname) {
    const prevPath = state.path;
    const prevElement = outletRef.current;
    const towable =
      navigationType === NavigationType.Push &&
      isTowPair(prevPath, pathname) &&
      prevElement != null &&
      viewportRef.current != null &&
      !prefersReducedMotion();

    if (towable && viewportRef.current) {
      const now = performance.now();
      const flightAt = flightAtRef.current;
      const age = flightAt === null ? null : now - flightAt;
      const shiftMs =
        age !== null && age >= 0 && age <= MAX_ANCHOR_AGE_MS
          ? Math.min(age, FLIGHT_TIMING.TOW_START)
          : 0;
      const freeze = measureFreeze(viewportRef.current);

      setState({
        path: pathname,
        phase: 'towing',
        runId: state.runId + 1,
        startedAt: now,
        prevPath,
        prevElement,
        frozenHeight: freeze.height,
        scrollOffset: freeze.scrollOffset,
        wasScrollable: freeze.wasScrollable,
        shiftMs,
      });
    } else {
      // Any other navigation — including one that ARRIVES MID-TOW, e.g. Back to
      // /marketplace before the track has settled. Dropping straight to idle
      // unmounts the track and cancels its timers via runId; the outgoing slot
      // keeps its key, so Back genuinely returns the same live page instance.
      setState({ ...IDLE, path: pathname, runId: state.runId + 1 });
    }
  }

  useLayoutEffect(() => {
    outletRef.current = outlet;
  });

  // ---- phase timers -------------------------------------------------------
  const { phase, runId, startedAt, shiftMs } = state;
  useEffect(() => {
    if (phase === 'idle') return undefined;

    // Both deadlines are measured from the tow's own t=0, never chained off
    // each other, so a late timer cannot push the next beat out.
    const deadline =
      phase === 'towing'
        ? startedAt + towDelayMs(shiftMs) + FLIGHT_TIMING.TOW_DURATION
        : startedAt + CARRIAGE_END_MS - shiftMs;
    const remaining = Math.max(0, deadline - performance.now());

    const timer = window.setTimeout(() => {
      setState((s) => {
        if (s.runId !== runId || s.phase !== phase) return s;
        if (phase === 'towing') {
          // TOUCHDOWN. The track is held at exactly -100% by the animation's
          // fill, so dropping the outgoing slot and zeroing the transform in
          // this one commit leaves the incoming page on the very same pixels.
          return {
            ...s,
            phase: 'settling',
            prevPath: null,
            prevElement: null,
            frozenHeight: null,
            scrollOffset: 0,
          };
        }
        return { ...IDLE, path: s.path, runId: s.runId };
      });
    }, remaining);

    return () => window.clearTimeout(timer);
  }, [phase, runId, startedAt, shiftMs]);

  // ---- the scroll container ----------------------------------------------
  useLayoutEffect(() => {
    if (state.phase !== 'towing') return undefined;

    // The Search button that started this is inside the page now leaving. Let
    // it go before the slot is marked aria-hidden — aria-hidden over a focused
    // element is an a11y violation the browser will (rightly) complain about.
    // The old page unmounts on any normal navigation anyway, so focus landing
    // on the body is exactly today's behaviour.
    const active = document.activeElement;
    if (
      active instanceof HTMLElement &&
      outgoingRef.current?.contains(active)
    ) {
      active.blur();
    }

    // Freezing the clip window to one screen makes the container's content
    // exactly its own height, so its scrollbar disappears — and on any platform
    // with classic (non-overlay) scrollbars that widens the page by ~15px
    // mid-tow. Hold the gutter open for the duration and put it back after.
    if (!state.wasScrollable) return undefined;
    const target =
      (viewportRef.current && findScrollContainer(viewportRef.current)) ??
      document.documentElement;
    const previous = target.style.overflowY;
    target.style.overflowY = 'scroll';
    return () => {
      target.style.overflowY = previous;
    };
  }, [state.phase, state.runId, state.wasScrollable]);

  const towing = state.phase === 'towing';
  return {
    framed: isFramedPath(pathname),
    towing,
    arriving: state.phase !== 'idle',
    path: pathname,
    element: outlet,
    outgoing:
      towing && state.prevPath !== null
        ? { path: state.prevPath, element: state.prevElement }
        : null,
    frozenHeight: state.frozenHeight,
    scrollOffset: state.scrollOffset,
    towDelay: towDelayMs(state.shiftMs),
    carriageDelay: carriageDelayMs(state.shiftMs),
    viewportRef,
    outgoingRef,
  };
}
